import {
  Directive,
  TemplateRef,
  ViewContainerRef,
  effect,
  inject,
  input,
} from "@angular/core";
import { Capabilities, CurrentUserStore } from "../stores/current-user.store";

/**
 * Structural directive that renders the template only when the given capability
 * is enabled for the current user. When the capability input is undefined the
 * template renders unconditionally — handy for data-driven nav where some
 * items have a gate and others don't.
 *
 * Usage:
 *   <button *appHasCapability="'canManageTags'">Add tag</button>
 *   <a *appHasCapability="item.capability">{{ item.label }}</a>
 */
@Directive({
  selector: "[appHasCapability]",
  standalone: true,
})
export class HasCapabilityDirective {
  private readonly template = inject(TemplateRef<unknown>);
  private readonly viewContainer = inject(ViewContainerRef);
  private readonly store = inject(CurrentUserStore);

  readonly capability = input<keyof Capabilities | undefined>(undefined, {
    alias: "appHasCapability",
  });

  private rendered = false;

  constructor() {
    effect(() => {
      const cap = this.capability();
      const allowed = cap === undefined || !!this.store.capabilities()[cap];
      if (allowed && !this.rendered) {
        this.viewContainer.createEmbeddedView(this.template);
        this.rendered = true;
      } else if (!allowed && this.rendered) {
        this.viewContainer.clear();
        this.rendered = false;
      }
    });
  }
}
